/**
 * visibilityStrategy.ts
 *
 * The Visibility Strategy Workshop — free lead magnet covering the four
 * failure points that keep AI/ML candidates invisible to recruiters.
 *
 * Source: Visibility Strategy Workshop.pptx (17 slides).
 *
 * Positioning:
 *   - FREE, no login required (email-gated at the end for updates)
 *   - Top-of-funnel SEO content that routes into every other CareerForge product
 *   - Each failure point has an explicit "go deeper" CTA to the relevant product
 *
 * Product routing map:
 *   FP1 ATS Screening     → Resume Mastery
 *   FP2 LinkedIn Invisibility → LinkedIn Presence Builder
 *   FP3 GitHub Discoverability → Portfolio Site Builder (Lovable Prompt Kit)
 *   FP4 Network Activation → Career Transition Program (includes networking phase)
 */

import { Localized } from "../utils/localized";

// ---------------------------------------------------------------------------
// Interface
// ---------------------------------------------------------------------------

export interface SolutionStep {
  label: string;
  detail: string;
}

export interface PayoffStat {
  value: string;    // "75%", "3x", "5–10x"
  label: string;    // "of resumes filtered by ATS"
}

export interface FailurePoint {
  number: number;
  slug: string;
  title: string;
  shortTitle: string;
  icon: string;  // emoji for now; designer can swap

  problem: {
    headline: string;
    stat?: string;
    detail: string;
  };

  solution: {
    headline: string;
    criticalInsight: string;
    steps: SolutionStep[];
    doExample?: string;
    dontExample?: string;
  };

  payoff: PayoffStat[];

  goDeeper: {
    headline: string;
    description: string;
    productName: string;
    productSlug: string;  // internal route
    ctaLabel: string;
  };
}

export interface WeeklyActionItem {
  timeframe: "this_week" | "monthly";
  label: string;
}

// ---------------------------------------------------------------------------
// Context — The Hiring Reality
// ---------------------------------------------------------------------------

export const HIRING_REALITY_STATS: Localized<PayoffStat[]> = {
  en: [
    { value: "75%", label: "of recruiters say a portfolio is the deciding factor" },
    { value: "73%", label: "of applicants admit to using AI to embellish resumes" },
    { value: "85%", label: "of jobs filled via networking, not cold applications" },
    { value: "2%", label: "success rate of cold applications without referral" },
  ],
  es: [
    {
      "value": "75%",
      "label": "de los reclutadores afirma que un portafolio es el factor decisivo"
    },
    {
      "value": "73%",
      "label": "de los candidatos admite usar IA para adornar sus currículums"
    },
    {
      "value": "85%",
      "label": "de los empleos se cubren mediante networking, no con postulaciones en frío"
    },
    {
      "value": "2%",
      "label": "tasa de éxito de las postulaciones en frío sin recomendación"
    }
  ],
};

export const HIRING_REALITY_TALKING_POINTS: Localized<string[]> = {
  en: [
    "Verification of Ability — with 73% of applicants using AI to embellish resumes, employers need proof you can actually build.",
    "The MLOps Skill Gap — companies are drowning in theory-heavy applicants. They need deployment proof.",
    "GenAI Recency — portfolios prove you have 2024–2026 skills (RAG, fine-tuning, agentic systems) that senior engineers may lack.",
    "The gap is not between 'qualified' and 'unqualified' — it is between 'discoverable' and 'invisible.'",
  ],
  es: [
    "Verificación de Capacidad: con el 73% de los solicitantes usando IA para adornar sus currículums, los empleadores necesitan pruebas de que realmente puedes construir.",
    "La Brecha de Habilidades en MLOps: las empresas están saturadas de solicitantes con exceso de teoría. Necesitan pruebas de despliegue.",
    "Vigencia en GenAI: los portafolios demuestran que tienes habilidades de 2024–2026 (RAG, fine-tuning, sistemas agénticos) que los ingenieros senior podrían no tener.",
    "La brecha no está entre 'calificado' y 'no calificado', sino entre 'visible' e 'invisible.'"
  ],
};

// ---------------------------------------------------------------------------
// The 4 Failure Points
// ---------------------------------------------------------------------------

export const FAILURE_POINTS: Localized<FailurePoint[]> = {
  en: [

  // FP1 — ATS SCREENING
  {
    number: 1,
    slug: "ats-screening",
    shortTitle: "ATS Screening",
    title: "ATS Screening Failure",
    icon: "📄",
    problem: {
      headline: "ATS systems filter you out before a human ever sees the resume.",
      stat: "75%",
      detail: "Roughly 75% of resumes are rejected by Applicant Tracking Systems at large employers — not for lack of qualification, but for failing the literal-string keyword match. Modern ATS uses semantic analysis. It understands context. But it still scores you against the exact terminology of the JD. If the JD says 'LangGraph' and your resume says 'LangChain Graph DSL,' the parser doesn't know those are the same thing.",
    },
    solution: {
      headline: "Keyword optimization with semantic placement",
      criticalInsight:
        "Don't just list keywords. Place them in two specific contexts: (1) Technical Skills section (literal lists), (2) Project and experience descriptions (natural context). The ATS scores both occurrence and context.",
      steps: [
        {
          label: "Collect & Extract",
          detail: "Find 5–10 target JDs. Extract the exact keywords the postings use. Pay special attention to agentic AI terminology: LangGraph, LangChain, AutoGen, MCP, A2A, RAG, vector databases, multi-agent systems, tool use.",
        },
        {
          label: "Analyze Match Score",
          detail: "Use a tool like Jobscan to test your resume against the JD. Target a 70–80% match. Below 60% means you are missing critical terms; above 80% usually signals stuffing.",
        },
        {
          label: "Strategic Placement",
          detail: "Place each target keyword in BOTH your Skills section AND at least one experience bullet. Never include a skill in the list that doesn't appear in context somewhere else on the resume — it looks disconnected.",
        },
        {
          label: "ATS-safe formatting",
          detail: "Single-column layout. Standard headings only ('Work Experience', 'Education', 'Skills'). No tables, text boxes, or columns — most ATS parsers skip them. No icons, graphics, or stylized fonts in headers.",
        },
      ],
      dontExample: "Just list: Python, AWS, TensorFlow",
      doExample: "Developed computer vision pipeline using Python and deployed via Docker on AWS ECS, serving 2M daily inference requests through a TensorFlow model.",
    },
    payoff: [
      { value: "3x", label: "more human reviews when keyword match score exceeds 75%" },
      { value: "80%", label: "target Jobscan match score for competitive roles" },
    ],
    goDeeper: {
      headline: "Go deeper: Resume Mastery",
      description: "The Visibility Workshop covers the ATS principles. Resume Mastery is the full AI-native system — server-side gap analysis, targeted rewrites, company-specific exemplars, interview readiness, and a free ATS scorer that tests your résumé against a specific JD.",
      productName: "Resume Mastery: The AI/ML Job Search Edition",
      productSlug: "/resume-mastery",
      ctaLabel: "See Resume Mastery",
    },
  },

  // FP2 — LINKEDIN INVISIBILITY
  {
    number: 2,
    slug: "linkedin-invisibility",
    shortTitle: "LinkedIn Invisibility",
    title: "LinkedIn Search Invisibility",
    icon: "💼",
    problem: {
      headline: "Your LinkedIn headline is keeping you out of recruiter searches.",
      detail: "Headlines like 'Aspiring Data Scientist' or 'Passionate about AI' make you invisible to recruiter search. Recruiters source candidates by searching specific role titles + specializations. If your headline doesn't literally contain the terms they search for, you don't exist in their results — regardless of how good your experience is.",
    },
    solution: {
      headline: "SEO optimization for recruiter discovery",
      criticalInsight:
        "LinkedIn's search ranking is a function of three signals: headline keyword match, skill endorsements, and activity signals. You must hit all three. A perfect headline with no endorsements still ranks below a weaker headline with 10+ endorsements on the target skill.",
      steps: [
        {
          label: "Rewrite your headline with the formula",
          detail: "Format: [Target Role] | [Specialization] | [Key Skills]. Example: 'Agentic AI Engineer | Multi-Agent Systems for Healthcare | LangGraph · MCP · Production MLOps'. Every term is a literal search keyword.",
        },
        {
          label: "Optimize the About section",
          detail: "First 3–4 lines MUST include target role keywords and domain expertise — this is what appears in recruiter preview cards. Then tell your career transition story emphasizing VALUE, not desperation. Close with specific project outcomes and metrics.",
        },
        {
          label: "Skills & Endorsements",
          detail: "Add 10–15 skills matching target JDs. Ask peers to endorse the top 5–7. Skills with 10+ endorsements rank materially higher in search.",
        },
        {
          label: "Enable 'Open to Work' — Recruiters Only",
          detail: "This is the Recruiters-Only visibility (not the public green banner). It increases recruiter InMail visibility 2x without signaling unemployment to your current network.",
        },
        {
          label: "Daily engagement",
          detail: "Comment thoughtfully on 2–3 posts per day in your target domain. This signals the LinkedIn algorithm to surface your profile. Passive profiles rank lower regardless of content quality.",
        },
      ],
      dontExample: "Aspiring Data Scientist | Open to Opportunities",
      doExample: "Agentic AI Engineer | Production RAG + Multi-Agent Systems | LangGraph · Claude · Python",
    },
    payoff: [
      { value: "5–10x", label: "profile view increase after headline + About rewrite" },
      { value: "5–15", label: "recruiter InMails per month (from near-zero baseline)" },
    ],
    goDeeper: {
      headline: "Go deeper: LinkedIn Presence Builder",
      description: "The Visibility Workshop covers the fundamentals. LinkedIn Presence Builder provides the full prompt kit for headline rewrites, About-section architecture, 4-week content calendar with post templates, and the outreach scripts recruiters actually respond to.",
      productName: "LinkedIn Presence Builder",
      productSlug: "/linkedin-presence",  // future route
      ctaLabel: "See LinkedIn Presence Builder",
    },
  },

  // FP3 — GITHUB DISCOVERABILITY
  {
    number: 3,
    slug: "github-discoverability",
    shortTitle: "GitHub Discoverability",
    title: "GitHub Discoverability Failure",
    icon: "💻",
    problem: {
      headline: "Recruiters search GitHub by tags and READMEs — your repos are invisible.",
      stat: "45%",
      detail: "Recruiters at AI/ML companies use GitHub sourcing tools to find candidates by repository topics, languages, and contribution patterns. 45% of recruiters say they discard portfolios with poor documentation. An undocumented repo, even a technically excellent one, is invisible to the sourcing pipeline — and looks unprofessional to reviewers who do find it.",
    },
    solution: {
      headline: "Build a developer landing page + documented projects",
      criticalInsight:
        "Your GitHub profile is a landing page, not a code dump. Create a special 'profile README' repository (named exactly after your username) that serves as your developer homepage. Every pinned repository must be a polished, documented deliverable — not a notebook with '#TODO' comments.",
      steps: [
        {
          label: "Create the Profile README",
          detail: "Create a repo with the EXACT same name as your username. GitHub will automatically display its README on your profile page. Include: professional header + bio, tech stack with Shields.io icons, 3–4 pinned project cards with thumbnails, contribution stats widget.",
        },
        {
          label: "Document every pinned project (7-part README)",
          detail: "Each project README needs: (1) clear, descriptive title, (2) problem description — what this solves, (3) tech stack with versions, (4) installation and run instructions, (5) results with metrics and business impact, (6) live demo or GIF link, (7) learnings and challenges overcome.",
        },
        {
          label: "Add repository topics (tags)",
          detail: "Every repo needs 5–10 topic tags. For agentic AI: machine-learning, langgraph, rag, multi-agent, agentic-ai, llm, python, pytorch. Recruiters search by these tags — untagged repos don't surface.",
        },
        {
          label: "Pin strategically",
          detail: "Pin 3–6 repos that tell a coherent story about your specialization. A random assortment signals unfocused. Three agentic AI projects with strong READMEs beat ten miscellaneous tutorials.",
        },
      ],
    },
    payoff: [
      { value: "45%", label: "of recruiters discard portfolios with poor documentation" },
      { value: "3–6", label: "pinned projects is the right count — more dilutes your specialization story" },
    ],
    goDeeper: {
      headline: "Go deeper: Portfolio Kit",
      description: "The Visibility Workshop covers GitHub discoverability. The Portfolio Kit ships both halves: three project archetypes with eval harnesses and 7-domain evidence mapping (Projects Playbook), plus a Lovable prompt kit for an Honest-Fit portfolio site with an AI chatbot trained on your career context.",
      productName: "Portfolio Kit — Playbook + Lovable Prompt Kit",
      productSlug: "/portfolio-kit",
      ctaLabel: "Open Portfolio Kit",
    },
  },

  // FP4 — NETWORK ACTIVATION
  {
    number: 4,
    slug: "network-activation",
    shortTitle: "Network Activation",
    title: "Network Activation Failure",
    icon: "🤝",
    problem: {
      headline: "You're applying cold when 85% of jobs are filled through networking.",
      stat: "2%",
      detail: "Cold applications have a 2% interview rate. Referred candidates have a 40%+ interview rate. The math is not close. Most candidates know networking matters but don't run it systematically — they send occasional 'please refer me' messages that get ignored. Systematic networking is a weekly operating practice, not a sporadic favor request.",
    },
    solution: {
      headline: "Multi-channel application + outreach system",
      criticalInsight:
        "Lead with curiosity, not job-seeking. The message that gets a response is 'I admired your work on X and want to learn about your path' — NOT 'can you refer me to your company?' Build relationships before you need them. The ask comes months later, if at all.",
      steps: [
        {
          label: "Weekly networking targets",
          detail: "New connections: 20 per week (alumni, recruiters, practitioners at target companies). Meaningful engagements: 10 thoughtful comments per week on posts by target contacts. Informational interviews: 3–5 per month (15-minute calls, not 'coffee chats').",
        },
        {
          label: "The outreach template",
          detail: "Principle: lead with curiosity, not job-seeking. Template: 'Hi [Name], I noticed you work on [specific project] at [Company]. I recently built a [similar project] and would love to learn from your path. Would you be open to a 15-min call?' The specificity is what gets the response.",
        },
        {
          label: "Multi-channel application strategy",
          detail: "Stop relying on job boards. Diversify: (1) Company career pages — often bypass restrictive ATS filters. (2) Hiring manager outreach — apply via the portal, then DM the manager on LinkedIn within the hour. (3) Direct recruiter outreach — search 'AI Recruiter at [Company]' and send your portfolio. (4) Startup channels — AngelList/Wellfound. 20–40 applicants vs. 200+ at Big Tech.",
        },
        {
          label: "Persistence is the differentiator",
          detail: "Success comes from month 6–8, not month 1. Most candidates stop networking after 60 days of no offers and attribute the failure to the market. The candidates who persist through the 'silent middle' period are the ones who land.",
        },
      ],
    },
    payoff: [
      { value: "20x", label: "higher interview rate when referred vs. cold applying" },
      { value: "60+", label: "monthly applications across multi-channel (not just job boards)" },
    ],
    goDeeper: {
      headline: "Go deeper: Career Transition Program",
      description: "The Visibility Workshop covers the networking fundamentals. Career Transition is the orchestrator — a 15-question diagnostic generates your personalized roadmap (Claude-powered), plus 6 transition-specific frameworks: narrative construction, financial bridge planning, pivoter-specific negotiation, first 90 days, and the internal-vs-external decision. Routes into every other CareerForge product at the right moment.",
      productName: "Career Transition",
      productSlug: "/career-transition",
      ctaLabel: "Open Career Transition",
    },
  },
  ],
  es: [
    {
      "number": 1,
      "slug": "ats-screening",
      "shortTitle": "Filtrado ATS",
      "title": "Fracaso en el filtrado ATS",
      "icon": "📄",
      "problem": {
        "headline": "Los sistemas ATS te descartan antes de que un humano vea siquiera tu currículum.",
        "stat": "75%",
        "detail": "Aproximadamente el 75% de los currículums son rechazados por los sistemas de seguimiento de candidatos (ATS) en las grandes empresas, no por falta de cualificación, sino por no superar la coincidencia literal de palabras clave. Los ATS modernos usan análisis semántico. Entienden el contexto. Pero aun así te evalúan según la terminología exacta de la descripción del puesto. Si la descripción dice 'LangGraph' y tu currículum dice 'LangChain Graph DSL', el analizador no sabe que son lo mismo."
      },
      "solution": {
        "headline": "Optimización de palabras clave con colocación semántica",
        "criticalInsight": "No te limites a listar palabras clave. Colócalas en dos contextos específicos: (1) la sección de Habilidades Técnicas (listas literales), (2) las descripciones de proyectos y experiencia (contexto natural). El ATS evalúa tanto la aparición como el contexto.",
        "steps": [
          {
            "label": "Recopilar y extraer",
            "detail": "Encuentra entre 5 y 10 descripciones de puestos objetivo. Extrae las palabras clave exactas que usan las publicaciones. Presta especial atención a la terminología de la agentic AI: LangGraph, LangChain, AutoGen, MCP, A2A, RAG, bases de datos vectoriales, sistemas multiagente, uso de herramientas."
          },
          {
            "label": "Analizar la puntuación de coincidencia",
            "detail": "Usa una herramienta como Jobscan para probar tu currículum frente a la descripción del puesto. Apunta a una coincidencia del 70–80%. Por debajo del 60% significa que te faltan términos críticos; por encima del 80% suele indicar saturación de palabras clave."
          },
          {
            "label": "Colocación estratégica",
            "detail": "Coloca cada palabra clave objetivo TANTO en tu sección de Habilidades COMO en al menos un punto de tu experiencia. Nunca incluyas una habilidad en la lista que no aparezca en contexto en algún otro lugar del currículum: parece desconectada."
          },
          {
            "label": "Formato compatible con ATS",
            "detail": "Diseño de una sola columna. Solo encabezados estándar ('Experiencia Laboral', 'Educación', 'Habilidades'). Sin tablas, cuadros de texto ni columnas: la mayoría de los analizadores ATS los omiten. Sin iconos, gráficos ni fuentes estilizadas en los encabezados."
          }
        ],
        "dontExample": "Solo listar: Python, AWS, TensorFlow",
        "doExample": "Desarrollé un pipeline de visión por computadora usando Python y lo desplegué mediante Docker en AWS ECS, sirviendo 2M de solicitudes de inferencia diarias a través de un modelo de TensorFlow."
      },
      "payoff": [
        {
          "value": "3x",
          "label": "más revisiones humanas cuando la puntuación de coincidencia de palabras clave supera el 75%"
        },
        {
          "value": "80%",
          "label": "puntuación de coincidencia objetivo en Jobscan para puestos competitivos"
        }
      ],
      "goDeeper": {
        "headline": "Profundiza: Resume Mastery",
        "description": "El Visibility Workshop cubre los principios del ATS. Resume Mastery es el sistema completo nativo de IA: análisis de brechas del lado del servidor, reescrituras dirigidas, ejemplos específicos por empresa, preparación para entrevistas y un evaluador de ATS gratuito que prueba tu currículum frente a una descripción de puesto específica.",
        "productName": "Resume Mastery: The AI/ML Job Search Edition",
        "productSlug": "/resume-mastery",
        "ctaLabel": "Ver Resume Mastery"
      }
    },
    {
      "number": 2,
      "slug": "linkedin-invisibility",
      "shortTitle": "Invisibilidad en LinkedIn",
      "title": "Invisibilidad en las búsquedas de LinkedIn",
      "icon": "💼",
      "problem": {
        "headline": "Tu titular de LinkedIn te está dejando fuera de las búsquedas de los reclutadores.",
        "detail": "Titulares como 'Aspirante a Científico de Datos' o 'Apasionado por la IA' te hacen invisible en las búsquedas de los reclutadores. Los reclutadores buscan candidatos mediante títulos de puestos específicos + especializaciones. Si tu titular no contiene literalmente los términos que buscan, no existes en sus resultados, sin importar lo buena que sea tu experiencia."
      },
      "solution": {
        "headline": "Optimización SEO para el descubrimiento por reclutadores",
        "criticalInsight": "El ranking de búsqueda de LinkedIn es una función de tres señales: coincidencia de palabras clave en el titular, recomendaciones de habilidades y señales de actividad. Debes acertar en las tres. Un titular perfecto sin recomendaciones sigue quedando por debajo de un titular más débil con más de 10 recomendaciones en la habilidad objetivo.",
        "steps": [
          {
            "label": "Reescribe tu titular con la fórmula",
            "detail": "Formato: [Puesto Objetivo] | [Especialización] | [Habilidades Clave]. Ejemplo: 'Agentic AI Engineer | Sistemas Multiagente para el Sector Salud | LangGraph · MCP · MLOps en Producción'. Cada término es una palabra clave literal de búsqueda."
          },
          {
            "label": "Optimiza la sección Acerca de",
            "detail": "Las primeras 3–4 líneas DEBEN incluir palabras clave del puesto objetivo y experiencia en el dominio; esto es lo que aparece en las tarjetas de vista previa para los reclutadores. Luego cuenta tu historia de Career Transition enfatizando el VALOR, no la desesperación. Cierra con resultados y métricas específicas de proyectos."
          },
          {
            "label": "Habilidades y recomendaciones",
            "detail": "Añade entre 10 y 15 habilidades que coincidan con los puestos objetivo. Pide a colegas que recomienden las 5–7 principales. Las habilidades con más de 10 recomendaciones se posicionan considerablemente más alto en las búsquedas."
          },
          {
            "label": "Activa 'Abierto a oportunidades' — Solo Reclutadores",
            "detail": "Es la visibilidad Solo para Reclutadores (no el banner verde público). Aumenta la visibilidad de tus InMails de reclutadores en 2x sin señalar desempleo a tu red actual."
          },
          {
            "label": "Interacción diaria",
            "detail": "Comenta de forma reflexiva en 2–3 publicaciones al día en tu dominio objetivo. Esto le indica al algoritmo de LinkedIn que destaque tu perfil. Los perfiles pasivos se posicionan más abajo sin importar la calidad del contenido."
          }
        ],
        "dontExample": "Aspirante a Científico de Datos | Abierto a Oportunidades",
        "doExample": "Agentic AI Engineer | RAG en Producción + Sistemas Multiagente | LangGraph · Claude · Python"
      },
      "payoff": [
        {
          "value": "5–10x",
          "label": "aumento de vistas del perfil tras reescribir el titular + la sección Acerca de"
        },
        {
          "value": "5–15",
          "label": "InMails de reclutadores al mes (desde una base cercana a cero)"
        }
      ],
      "goDeeper": {
        "headline": "Profundiza: LinkedIn Presence Builder",
        "description": "El Visibility Workshop cubre los fundamentos. LinkedIn Presence Builder ofrece el kit completo de prompts para reescribir titulares, arquitectura de la sección Acerca de, un calendario de contenido de 4 semanas con plantillas de publicaciones y los guiones de contacto a los que los reclutadores realmente responden.",
        "productName": "LinkedIn Presence Builder",
        "productSlug": "/linkedin-presence",
        "ctaLabel": "Ver LinkedIn Presence Builder"
      }
    },
    {
      "number": 3,
      "slug": "github-discoverability",
      "shortTitle": "Descubribilidad en GitHub",
      "title": "Fracaso en la descubribilidad en GitHub",
      "icon": "💻",
      "problem": {
        "headline": "Los reclutadores buscan en GitHub por etiquetas y READMEs; tus repos son invisibles.",
        "stat": "45%",
        "detail": "Los reclutadores de empresas de IA/ML usan herramientas de sourcing de GitHub para encontrar candidatos por temas de repositorio, lenguajes y patrones de contribución. El 45% de los reclutadores dicen que descartan portafolios con mala documentación. Un repo sin documentar, incluso uno técnicamente excelente, es invisible para el proceso de sourcing y parece poco profesional a los revisores que sí lo encuentran."
      },
      "solution": {
        "headline": "Crea una página de presentación de desarrollador + proyectos documentados",
        "criticalInsight": "Tu perfil de GitHub es una página de presentación, no un vertedero de código. Crea un repositorio especial de 'profile README' (nombrado exactamente igual que tu nombre de usuario) que sirva como tu página de inicio de desarrollador. Cada repositorio fijado debe ser un entregable pulido y documentado, no un notebook con comentarios '#TODO'.",
        "steps": [
          {
            "label": "Crea el Profile README",
            "detail": "Crea un repo con EXACTAMENTE el mismo nombre que tu nombre de usuario. GitHub mostrará automáticamente su README en la página de tu perfil. Incluye: encabezado profesional + biografía, stack tecnológico con iconos de Shields.io, 3–4 tarjetas de proyectos fijados con miniaturas, y un widget de estadísticas de contribución."
          },
          {
            "label": "Documenta cada proyecto fijado (README de 7 partes)",
            "detail": "Cada README de proyecto necesita: (1) título claro y descriptivo, (2) descripción del problema — qué resuelve esto, (3) stack tecnológico con versiones, (4) instrucciones de instalación y ejecución, (5) resultados con métricas e impacto de negocio, (6) enlace a demo en vivo o GIF, (7) aprendizajes y desafíos superados."
          },
          {
            "label": "Añade temas (etiquetas) al repositorio",
            "detail": "Cada repo necesita entre 5 y 10 etiquetas de tema. Para agentic AI: machine-learning, langgraph, rag, multi-agent, agentic-ai, llm, python, pytorch. Los reclutadores buscan por estas etiquetas; los repos sin etiquetar no aparecen."
          },
          {
            "label": "Fija estratégicamente",
            "detail": "Fija de 3 a 6 repos que cuenten una historia coherente sobre tu especialización. Una selección aleatoria transmite falta de enfoque. Tres proyectos de agentic AI con READMEs sólidos superan a diez tutoriales variados."
          }
        ]
      },
      "payoff": [
        {
          "value": "45%",
          "label": "de los reclutadores descartan portafolios con mala documentación"
        },
        {
          "value": "3–6",
          "label": "proyectos fijados es la cantidad adecuada — más diluye tu historia de especialización"
        }
      ],
      "goDeeper": {
        "headline": "Profundiza: Portfolio Kit",
        "description": "El Visibility Workshop cubre la descubribilidad en GitHub. El Portfolio Kit ofrece ambas mitades: tres arquetipos de proyectos con arneses de evaluación y mapeo de evidencia de 7 dominios (Projects Playbook), más un kit de prompts de Lovable para un sitio de portafolio Honest-Fit con un chatbot de IA entrenado en tu contexto profesional.",
        "productName": "Portfolio Kit — Playbook + Lovable Prompt Kit",
        "productSlug": "/portfolio-kit",
        "ctaLabel": "Abrir Portfolio Kit"
      }
    },
    {
      "number": 4,
      "slug": "network-activation",
      "shortTitle": "Activación de red",
      "title": "Fracaso en la activación de red",
      "icon": "🤝",
      "problem": {
        "headline": "Estás aplicando en frío cuando el 85% de los empleos se cubren mediante networking.",
        "stat": "2%",
        "detail": "Las postulaciones en frío tienen una tasa de entrevista del 2%. Los candidatos referidos tienen una tasa de entrevista superior al 40%. La diferencia no es sutil. La mayoría de los candidatos saben que el networking importa, pero no lo ejecutan de forma sistemática: envían mensajes ocasionales de 'por favor, refiéreme' que son ignorados. El networking sistemático es una práctica operativa semanal, no una solicitud esporádica de favores."
      },
      "solution": {
        "headline": "Sistema de postulación multicanal + contacto",
        "criticalInsight": "Lidera con curiosidad, no con búsqueda de empleo. El mensaje que obtiene respuesta es 'Admiré tu trabajo en X y quiero conocer tu trayectoria', NO '¿puedes referirme a tu empresa?'. Construye relaciones antes de necesitarlas. La petición llega meses después, si es que llega.",
        "steps": [
          {
            "label": "Objetivos semanales de networking",
            "detail": "Nuevas conexiones: 20 por semana (exalumnos, reclutadores, profesionales en empresas objetivo). Interacciones significativas: 10 comentarios reflexivos por semana en publicaciones de contactos objetivo. Entrevistas informativas: 3–5 por mes (llamadas de 15 minutos, no 'charlas de café')."
          },
          {
            "label": "La plantilla de contacto",
            "detail": "Principio: lidera con curiosidad, no con búsqueda de empleo. Plantilla: 'Hola [Nombre], vi que trabajas en [proyecto específico] en [Empresa]. Recientemente construí un [proyecto similar] y me encantaría aprender de tu trayectoria. ¿Estarías abierto a una llamada de 15 min?'. La especificidad es lo que genera la respuesta."
          },
          {
            "label": "Estrategia de postulación multicanal",
            "detail": "Deja de depender de los portales de empleo. Diversifica: (1) Páginas de carreras de las empresas — a menudo evitan los filtros ATS restrictivos. (2) Contacto con el gerente de contratación — postula por el portal y luego envíale un DM al gerente en LinkedIn dentro de la hora. (3) Contacto directo con reclutadores — busca 'AI Recruiter at [Empresa]' y envía tu portafolio. (4) Canales de startups — AngelList/Wellfound. 20–40 postulantes frente a más de 200 en las grandes tecnológicas."
          },
          {
            "label": "La persistencia es el factor diferenciador",
            "detail": "El éxito llega en el mes 6–8, no en el mes 1. La mayoría de los candidatos dejan de hacer networking tras 60 días sin ofertas y atribuyen el fracaso al mercado. Los candidatos que persisten durante el período del 'silencio intermedio' son los que consiguen el puesto."
          }
        ]
      },
      "payoff": [
        {
          "value": "20x",
          "label": "mayor tasa de entrevista cuando eres referido frente a postular en frío"
        },
        {
          "value": "60+",
          "label": "postulaciones mensuales en múltiples canales (no solo portales de empleo)"
        }
      ],
      "goDeeper": {
        "headline": "Profundiza: Career Transition Program",
        "description": "El Visibility Workshop cubre los fundamentos del networking. Career Transition es el orquestador: un diagnóstico de 15 preguntas genera tu hoja de ruta personalizada (impulsada por Claude), más 6 frameworks específicos de transición: construcción de narrativa, planificación del puente financiero, negociación específica para quienes cambian de carrera, los primeros 90 días y la decisión interno vs. externo. Te dirige a cada uno de los demás productos de CareerForge en el momento adecuado.",
        "productName": "Career Transition",
        "productSlug": "/career-transition",
        "ctaLabel": "Abrir Career Transition"
      }
    }
  ],
};

// ---------------------------------------------------------------------------
// Weekly Action Plan
// ---------------------------------------------------------------------------

export const WEEKLY_ACTION_ITEMS: Localized<WeeklyActionItem[]> = {
  en: [
    { timeframe: "this_week", label: "Run your resume through Jobscan against 3 target JDs. Target 70%+ match." },
    { timeframe: "this_week", label: "Rewrite your LinkedIn headline using the [Role] | [Specialization] | [Skills] formula." },
    { timeframe: "this_week", label: "Create your GitHub Profile README (repo named after your username)." },
    { timeframe: "this_week", label: "Send 10 connection requests to AI/ML practitioners in your target domain." },
    { timeframe: "monthly", label: "Conduct 3–5 informational interviews per month (15-min calls with target company contacts)." },
    { timeframe: "monthly", label: "Secure 1–2 referrals per month through systematic network building." },
    { timeframe: "monthly", label: "Send 60+ applications per month across multi-channel (career pages, hiring manager outreach, direct recruiter, startup channels)." },
    { timeframe: "monthly", label: "Ship one new documented project to your GitHub portfolio." },
  ],
  es: [
    {
      "timeframe": "this_week",
      "label": "Analiza tu currículum con Jobscan frente a 3 descripciones de puesto objetivo. Apunta a una coincidencia del 70%+."
    },
    {
      "timeframe": "this_week",
      "label": "Reescribe el titular de tu LinkedIn usando la fórmula [Rol] | [Especialización] | [Habilidades]."
    },
    {
      "timeframe": "this_week",
      "label": "Crea el README de tu perfil de GitHub (repositorio con el nombre de tu usuario)."
    },
    {
      "timeframe": "this_week",
      "label": "Envía 10 solicitudes de conexión a profesionales de AI/ML en tu dominio objetivo."
    },
    {
      "timeframe": "monthly",
      "label": "Realiza de 3 a 5 entrevistas informativas al mes (llamadas de 15 min con contactos de empresas objetivo)."
    },
    {
      "timeframe": "monthly",
      "label": "Consigue de 1 a 2 referencias al mes mediante la construcción sistemática de tu red de contactos."
    },
    {
      "timeframe": "monthly",
      "label": "Envía más de 60 solicitudes al mes por múltiples canales (páginas de empleo, contacto con responsables de contratación, reclutadores directos, canales de startups)."
    },
    {
      "timeframe": "monthly",
      "label": "Publica un nuevo proyecto documentado en tu portafolio de GitHub."
    }
  ],
};

// ---------------------------------------------------------------------------
// Closing framing
// ---------------------------------------------------------------------------

export const CLOSING_NOTE: Localized<string> = {
  en: "Success comes from persistence through month 6–8. The candidates who win are not the most qualified — they are the most discoverable and most consistent. This workshop gives you the discoverability system; the product suite gives you the weekly execution.",
  es: "El éxito proviene de la constancia durante los meses 6 a 8. Los candidatos que ganan no son los más calificados, sino los más visibles y consistentes. Este taller te brinda el sistema de visibilidad; la suite de productos te da la ejecución semanal.",
};
